// Where each page was scrolled to, kept per history entry: going back (or forward) returns there
// (the owner: "going back scrolls to the top instead of where one was"). The browser's own
// restoring comes too early here — the page it returns to is drawn later (its code and its data
// arrive after the address changes) — so the place is kept by the app and put back once the page
// is tall enough. A new page starts at its top; a change of a page's own details (a filter, an open
// transaction) keeps the place. Kept for the tab's session, so a reload returns there too.

type ScrollMove = { kind: 'restore'; y: number } | { kind: 'top' } | { kind: 'keep' };

/** What to do with the scroll when the address changes. */
export function scrollMove(previousPage: string, nextPage: string, traversal: boolean, saved: number | undefined): ScrollMove {
  if (traversal && saved !== undefined) return { kind: 'restore', y: saved };
  return previousPage === nextPage ? { kind: 'keep' } : { kind: 'top' };
}

const STORE = 'fd.scrollPlaces';
/** How long to wait for a page to grow tall enough for its place. */
const PATIENCE_MS = 3000;

export function installScrollMemory(pageOf: (hash: string) => string): void {
  if (typeof window === 'undefined' || !('scrollRestoration' in history)) return;
  history.scrollRestoration = 'manual';
  let places: Record<string, number> = {};
  try {
    places = JSON.parse(sessionStorage.getItem(STORE) ?? '{}') as Record<string, number>;
  } catch {
    places = {};
  }
  const keyOf = () => (history.state as { fdScroll?: string } | null)?.fdScroll;
  // A new entry gets its own name; one returned to already has it.
  const name = () => {
    const key = Math.random().toString(36).slice(2, 10);
    history.replaceState({ ...(history.state as object | null), fdScroll: key }, '');
    return key;
  };
  let current = keyOf() ?? name();
  let page = pageOf(location.hash);
  let restoring = 0;
  let writing: ReturnType<typeof setTimeout> | null = null;

  const stop = () => {
    if (restoring) cancelAnimationFrame(restoring);
    restoring = 0;
  };
  // Put the page back at `y` as soon as it is tall enough (or as far as it goes after a while).
  const restore = (y: number) => {
    stop();
    const started = performance.now();
    const step = () => {
      const most = Math.max(0, document.documentElement.scrollHeight - innerHeight);
      scrollTo(0, Math.min(y, most));
      restoring = most >= y || performance.now() - started > PATIENCE_MS ? 0 : requestAnimationFrame(step);
    };
    restoring = requestAnimationFrame(step);
  };

  addEventListener(
    'scroll',
    () => {
      // While a place is being put back the scroll is the app's, not the owner's.
      if (restoring) return;
      places[current] = Math.round(scrollY);
      writing ??= setTimeout(() => {
        writing = null;
        try {
          sessionStorage.setItem(STORE, JSON.stringify(places));
        } catch {
          // storage unavailable: the places simply last until the tab reloads
        }
      }, 250);
    },
    { passive: true },
  );
  // The owner scrolling or reaching for the page takes over from a place still being put back.
  for (const type of ['wheel', 'touchstart', 'keydown', 'mousedown']) addEventListener(type, stop, { passive: true });

  addEventListener('hashchange', () => {
    const known = keyOf();
    const next = known ?? name();
    const nextPage = pageOf(location.hash);
    const move = scrollMove(page, nextPage, known !== undefined, places[next]);
    current = next;
    page = nextPage;
    if (move.kind === 'restore') restore(move.y);
    else if (move.kind === 'top') {
      stop();
      scrollTo(0, 0);
    }
  });

  // A reload returns to where the page was.
  const saved = places[current];
  if (saved) restore(saved);
}
